"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  cn,
  formatNumberWithDots,
  generatePaymentCallbackUrl,
} from "@/lib/utils";
import Portal from "../globalPortal";
import {
  IBaseModalProps,
  IDefaultPackage,
  IIncreaseWalletDepositPayload,
  IPackages,
  servicesCategoriesKeys,
  TCreditIncrementSchemaCombinePackage,
  TRawServiceApiKeys,
  TServiceNavigationItem,
} from "@/interfaces";
import { useLockBodyScroll } from "@uidotdev/usehooks";
import {
  useGetInquiry,
  useGetWalletBalance,
  useIncreaseWalletDepositMutation,
  usePurchasePackageMutation,
} from "@/hooks";
import { useSession } from "next-auth/react";
import { usePathname, useSearchParams } from "next/navigation";
import { useAppStore, useSecureStore } from "@/store";
import { CustomButton } from "@/components/ui";
import { BdAltArrowRight } from "solar-icon-react/bd";
import { usePackages } from "@/context/packagesContext";
import { getClientServiceKeyByAPIFlag } from "@/lib/adapters/serviceFlagAdapter";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import { useToastStore } from "@/store/toastStore";
import { coreClient } from "@/services";
import { getPackagesAdapter } from "@/lib/adapters/getPackagesAdapter";
import UserPackageRadioGroup from "../userPackageRadioGroup";
import InsufficientBalanceModal from "./insufficientBalanceModal";
import SufficientBalanceModal from "./sufficientBalanceModal";
import RadioPackageGroup from "@/components/packagesRadioGroup";
import Logo from "@/components/logo";
import styles from "./paymentCheckout.module.css";

interface Props extends IBaseModalProps {
  selectedService?: TServiceNavigationItem;
  paymentSubmission: () => void;
  handleBackBtn?: () => void;
  submissionError: Error | null;
  submissionLoading: boolean;
  showManualError?: string | null;
  persistedData?: Record<string, any>;
  onErrorReset?: () => void;
  payBtnId?: string;
  pageData?: string;
  packages?: IPackages[];
  onPackageSelect?: (packageId: string) => void;
  serviceId?: string | number;
  showData?: "category" | "service";
  allowedCategories?: servicesCategoriesKeys[];
  isTimerActive?: boolean;
  timerEndTime?: number | null;
  onTimerComplete?: () => void;
}

const FALLBACK_DEFAULT_PACKAGE: IDefaultPackage = {
  id: "p0",
  name: "استعلام",
  price: "0",
};

export default function PaymentMethodModal({
  isOpen,
  onClose,
  selectedService,
  paymentSubmission,
  submissionError,
  showManualError = null,
  submissionLoading = false,
  persistedData,
  onErrorReset,
  payBtnId,
  packages,
  onPackageSelect,
  showData = "category",
  serviceId,
  allowedCategories,
  pageData,
  handleBackBtn,
  isTimerActive,
  onTimerComplete,
  timerEndTime,
}: Props) {
  const { packages: globalPackages, user: globalUser, setUser } = usePackages();

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isSuccessfulPaymentReturn =
    searchParams?.get("payment") === "1" &&
    searchParams?.get("payment_success") === "1";
  const hasAutoSubmittedRef = useRef(false);
  const { data: session } = useSession();
  const {
    data: walletBalanceData,
    isLoading: isWalletBalanceLoading,
    error: walletBalanceError,
  } = useGetWalletBalance(session?.user.token);
  const {
    mutate: purchasePackageFn,
    isPending: isPurchasing,
    error: purchaseError,
  } = usePurchasePackageMutation();
  const { data: inquiryData, isLoading: isInquiryLoading } = useGetInquiry({
    inquiry: (selectedService?.apiKey as TRawServiceApiKeys) || "",
    token: session?.user.token,
  });
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  // آیا globalUser مقدارش معلوم شده (نه undefined و نه null)
  const isGlobalUserKnown = globalUser !== undefined && globalUser !== null;
  // وقتی معلوم شد، ببین خالیه یا نه
  const globalUserIsArray = Array.isArray(globalUser);
  const globalUserIsEmpty = isGlobalUserKnown
    ? globalUserIsArray
      ? globalUser.length === 0
      : !globalUser
    : false;

  const pathName = usePathname();
  const { setPreviousPath } = useAppStore();
  const { setPersistedFields } = useSecureStore();
  const [saveCard, setSaveCard] = useState(false);
  const { setOpenToast } = useToastStore();
  const {
    isPending: isIncreaseSubmitting,
    mutate: increaseWalletDepositFn,
    error: increaseSubmittingError,
    reset: resetIncreaseWalletDeposit,
  } = useIncreaseWalletDepositMutation(session?.user.token);
  const walletBalance = Number(walletBalanceData?.data?.balance || 0);
  const serviceCost = Number(inquiryData?.data?.price_user || 0);

  const isWalletBalanceInsufficient =
    walletBalance === 0 || walletBalance < serviceCost;

  useLockBodyScroll();

  // Local state to hold packages fetched by service flag (inquiryFlag)
  const [flagPackages, setFlagPackages] = useState<IPackages[] | null>(null);

  // Fetch packages for the specific service flag when modal opens
  useEffect(() => {
    const apiKey = selectedService?.apiKey as string | undefined;
    if (!isOpen || !apiKey) return;

    let isCancelled = false;
    (async () => {
      try {
        const [packagesRes, inquiriesRes] = await Promise.all([
          coreClient.getPackagesData({
            token: session?.user.token,
            inquiryFlag: apiKey,
          }),
          coreClient.getInquiries(),
        ]);
        const adapted = getPackagesAdapter(
          (packagesRes as any)?.data,
          inquiriesRes?.data,
        );
        if (!isCancelled) {
          setFlagPackages(Array.isArray(adapted.list) ? adapted.list : []);

          if (adapted.user !== undefined) {
            setUser(adapted.user);
          }
        }
      } catch (e) {
        if (!isCancelled) setFlagPackages([]);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, selectedService?.apiKey, setUser]);

  // Reset errors when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset the mutation error
      resetIncreaseWalletDeposit();
      // Reset parent component errors
      if (onErrorReset) {
        onErrorReset();
      }
    }
  }, [isOpen, resetIncreaseWalletDeposit, onErrorReset]);

  // Reset errors when component unmounts
  useEffect(() => {
    return () => {
      resetIncreaseWalletDeposit();
      if (onErrorReset) {
        onErrorReset();
      }
    };
  }, [resetIncreaseWalletDeposit, onErrorReset]);

  const handleCreditIncrementSubmission = (
    data: TCreditIncrementSchemaCombinePackage & { is_topup?: boolean },
    bank?: string,
  ) => {
    const payload: IIncreaseWalletDepositPayload = {
      amount: data.amount,
      callback_url: data.callback,
      use_wallet: data.use_wallet,
      items: data.items,
      is_topup: data.is_topup,
    };

    increaseWalletDepositFn(
      { payload, bank },
      {
        onSuccess: (data) => {
          if (data.data) {
            setPreviousPath(pathName);
            if (persistedData) {
              setPersistedFields(persistedData);
            }
            window.location.href = data.data?.payment_link;
          }
        },
      },
    );
  };
  const defaultPackage: IDefaultPackage = {
    id: "p0",
    name: inquiryData?.data?.name || FALLBACK_DEFAULT_PACKAGE.name,
    price: formatNumberWithDots(serviceCost) || String(serviceCost),
  };
  const allPackages: IPackages[] = useMemo(
    () => flagPackages ?? (packages || globalPackages || []),
    [flagPackages, packages, globalPackages],
  );

  // Filter by serviceId
  const filterByServiceId = (items: IPackages[]) => {
    if (!serviceId) return items;
    return items.filter(
      (p) =>
        Array.isArray(p.serviceIds) && p.serviceIds.includes(Number(serviceId)),
    );
  };

  // Filter by categories
  const filterByCategories = (items: IPackages[]) => {
    // Use allowedCategories prop if provided, otherwise fall back to allowedCategory
    const categoriesToFilter =
      allowedCategories || (allowedCategory ? [allowedCategory] : null);

    if (!categoriesToFilter || categoriesToFilter.length === 0) return items;

    return items.filter(
      (p) =>
        Array.isArray(p.categories) &&
        p.categories.some((c) => categoriesToFilter.includes(c)),
    );
  };

  const allowedCategory: servicesCategoriesKeys | null = useMemo(() => {
    const apiKey = selectedService?.apiKey as string | undefined;
    if (!apiKey) return null;
    const enumKey = getClientServiceKeyByAPIFlag(apiKey);
    if (!enumKey) return null;
    return globalRawServicesData[enumKey]?.category ?? null;
  }, [selectedService?.apiKey]);

  const filteredPackages: IPackages[] = useMemo(() => {
    // Apply filter based on showData prop
    if (showData === "service") {
      return filterByServiceId(allPackages);
    }
    return filterByCategories(allPackages);
  }, [allPackages, showData, serviceId, allowedCategories, allowedCategory]);
  const hasPackageChoices =
    filteredPackages.length > 0 ||
    (isGlobalUserKnown && globalUserIsArray && globalUser.length > 0);

  const [selectedPackage, setSelectedPackage] = useState<
    IDefaultPackage | IPackages
  >(defaultPackage);

  useEffect(() => {
    if (isGlobalUserKnown && globalUserIsArray && globalUser.length > 0) {
      setSelectedPackage(globalUser[0]);
    }
  }, [isGlobalUserKnown, globalUserIsArray, globalUser]);

  // Handle package selection and notify parent
  const handlePackageSelect = (pkg: IPackages | IDefaultPackage) => {
    setSelectedPackage(pkg);
    // if (onPackageSelect) {
    //   onPackageSelect(pkg.campaign_id);
    // }
  };

  // Handle user package selection (different type)
  const handleUserPackageSelect = (pkg: IDefaultPackage | any) => {
    setSelectedPackage(pkg);
    if (onPackageSelect) {
      onPackageSelect(pkg.campaign_id);
    }
  };

  useEffect(() => {
    if (isOpen && onPackageSelect && selectedPackage) {
      if ("campaign_id" in selectedPackage && selectedPackage.campaign_id) {
        onPackageSelect(selectedPackage.campaign_id);
      } else if (selectedPackage.id === "p0") {
        // For default wallet option (p0), you might want to pass empty string or handle differently
        // Since p0 doesn't have a campaign_id, decide what to send
        onPackageSelect(""); // or onPackageSelect('p0') depending on your backend needs
      }
    }
  }, [isOpen, selectedPackage, onPackageSelect]);

  const [isUseWallet, setIsUseWallet] = useState(false);

  useEffect(() => {
    // Only reset if:
    // 1. It's a purchasable package (not wallet p0, not user package with remaining_days)
    // 2. That package no longer exists in filtered results
    const isUserPackage = (selectedPackage as any).remaining_days !== undefined;
    const isWalletOption = selectedPackage.id === "p0";

    if (
      !isUserPackage &&
      !isWalletOption &&
      "id" in selectedPackage &&
      filteredPackages.length > 0 &&
      !filteredPackages.some((p) => p.id === (selectedPackage as IPackages).id)
    ) {
      setSelectedPackage(defaultPackage);
    }
  }, [filteredPackages, selectedPackage]);

  useEffect(() => {
    if (
      !isOpen ||
      !isSuccessfulPaymentReturn ||
      isWalletBalanceLoading ||
      isInquiryLoading ||
      hasAutoSubmittedRef.current
    ) {
      return;
    }

    hasAutoSubmittedRef.current = true;
    paymentSubmission();
  }, [
    isOpen,
    isSuccessfulPaymentReturn,
    isWalletBalanceLoading,
    isInquiryLoading,
    paymentSubmission,
  ]);

  if (!isOpen || isWalletBalanceLoading || isInquiryLoading) return null;
  const handlePayment = (data?: any) => {
    if (data?.topup) {
      handleCreditIncrementSubmission(
        {
          amount: Number(data.digit),
          callback: generatePaymentCallbackUrl(pathname),
          use_wallet: false,
          is_topup: true,
        },
        data.bank,
      );
      return;
    }

    // Check if user selected wallet (p0) or has an existing package
    if (selectedPackage.id === "p0") {
      if (
        !isUseWallet &&
        walletBalanceData?.data?.payment_type !== "postpaid"
      ) {
        const paymentData = {
          amount: isWalletBalanceInsufficient
            ? Number(data?.digit)
            : serviceCost,
          callback: generatePaymentCallbackUrl(pathname),
          use_wallet: false,
        };
        const bank = data?.bank;
        handleCreditIncrementSubmission(paymentData, bank);
      } else {
        paymentSubmission();
      }
    } else if ((selectedPackage as any).remaining_days) {
      // User selected one of their existing packages - use it for inquiry
      paymentSubmission();
    } else {
      // User selected a new package to purchase
      // Check if the package has a price property (new packages should have this)
      if (!selectedPackage.price) {
        setOpenToast({
          variant: "default",
          message: "خطا در انتخاب بسته",
        });
        return;
      }

      const numericPrice = selectedPackage.price.replace(/[^\d]/g, "");
      const isWalletGreater = walletBalance >= Number(numericPrice);

      if (!session?.user.token) {
        setOpenToast({
          variant: "default",
          message: "برای خرید بسته ابتدا وارد شوید",
        });
        return;
      }

      // اگر میخواست از کیف پولش کم بشه
      if (isUseWallet) {
        // اگر مانده کیف پولش از قیمت پکیج بیشتر بود
        if (isWalletGreater) {
          purchasePackageFn(
            {
              purchasedPackageId: Number(selectedPackage?.id),
              token: session?.user.token,
            },
            {
              onSuccess: (res) => {
                setOpenToast({
                  variant: "success",
                  message: "خرید بسته با موفقیت انجام شد",
                });
                onClose();
              },
              onError: (err) => {
                console.error("خطا در خرید پکیج", err);
              },
            },
          );
        } else {
          // اگر مانده کیف پول از قیمت پکیج کمتر بود
          const finalPrice = Number(numericPrice) - walletBalance;
          const paymentData = {
            amount: Number(finalPrice),
            use_wallet: true,
            callback: generatePaymentCallbackUrl(pathname),
            items: {
              campaign: selectedPackage.id,
            },
          };
          handleCreditIncrementSubmission(paymentData);
        }
      } else {
        // اگه نمیخواست از کیف پولش کم بشه یا اصلا موجودیش صفر بود
        const paymentData = {
          amount: Number(numericPrice),
          use_wallet: false,
          callback: generatePaymentCallbackUrl(pathname),
          items: {
            campaign: selectedPackage.id,
          },
        };
        const bank = data.bank;
        handleCreditIncrementSubmission(paymentData, bank);
      }
    }

    setPreviousPath(pathName);
    useSecureStore.persist.clearStorage();
  };
  return (
    <Portal>
      <div className={styles.overlay} onMouseDown={onClose}>
        <section
          className={cn(
            styles.checkout,
            !hasPackageChoices && styles.singleCheckout,
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="service-payment-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header className={styles.header}>
            <div className={styles.headerTitle}>
              <span className={styles.brandMark}>
                <Logo width={35} height={35} />
              </span>
              <div>
                <h2 id="service-payment-title">تکمیل سفارش</h2>
                <p>انتخاب بسته و پرداخت</p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <span className={styles.secure}>پرداخت امن</span>
              <button
                className={styles.close}
                type="button"
                aria-label="بستن پنجره پرداخت"
                onClick={onClose}
              >
                <span className={styles.closeGlyph} aria-hidden="true" />
              </button>
            </div>
          </header>
          {pageData === "military" && (
            <div className="px-6 pt-3">
              <CustomButton
                variant="text"
                icon={<BdAltArrowRight size={20} />}
                className="mx-0! w-fit! px-0! text-xs!"
                onClick={handleBackBtn}
              >
                بازگشت به انتخاب سرویس
              </CustomButton>
            </div>
          )}
          <div className={styles.body}>
            {hasPackageChoices && (
              <section className={styles.plansPanel}>
                {isMounted &&
                  (isGlobalUserKnown &&
                  globalUserIsArray &&
                  globalUser.length > 0 ? (
                    <UserPackageRadioGroup
                      packages={globalUser}
                      selectedPackage={selectedPackage}
                      setSelectedPackage={handleUserPackageSelect}
                      walletBalance={walletBalance}
                    />
                  ) : (
                    <RadioPackageGroup
                      packages={filteredPackages}
                      defaultValue={defaultPackage}
                      selectedPackage={selectedPackage}
                      setSelectedPackage={handlePackageSelect}
                    />
                  ))}
              </section>
            )}
            <section
              className={cn(
                styles.summaryPanel,
                !hasPackageChoices && styles.singleSummary,
              )}
            >
              {isWalletBalanceInsufficient &&
              walletBalanceData?.data?.payment_type !== "postpaid" ? (
                <InsufficientBalanceModal
                  incrementBtnId={payBtnId}
                  onClose={onClose}
                  onSubmit={(data: any) => handlePayment(data)}
                  walletBalance={walletBalanceData?.data?.balance}
                  inquiryPrice={inquiryData?.data?.price_user}
                  error={
                    walletBalanceError ||
                    increaseSubmittingError ||
                    submissionError
                  }
                  isLoading={isIncreaseSubmitting}
                  selectedPackage={selectedPackage}
                  hasPackage={
                    selectedPackage?.id !== "p0" && Boolean(globalUser?.length)
                  }
                  showManualError={showManualError}
                />
              ) : (
                <SufficientBalanceModal
                  payBtnId={payBtnId}
                  onClose={onClose}
                  onSubmit={(data: any) => handlePayment(data)}
                  paymentType={walletBalanceData?.data?.payment_type}
                  error={submissionError}
                  serviceType={selectedService}
                  walletBalance={walletBalanceData?.data?.balance}
                  inquiryPrice={inquiryData?.data?.price_user}
                  isLoading={submissionLoading}
                  showManualError={showManualError}
                  selectedPackage={selectedPackage}
                  useWallet={isUseWallet}
                  walletHandler={setIsUseWallet}
                  hasPackage={
                    selectedPackage?.id !== "p0" && Boolean(globalUser?.length)
                  }
                  isTimerActive={isTimerActive}
                  timerEndTime={timerEndTime}
                  onTimerComplete={onTimerComplete}
                />
              )}
            </section>
          </div>
        </section>
      </div>
    </Portal>
  );
}
